import React from 'react';
import { useApp } from '@/context/AppContext';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { ChevronDown, Stethoscope, ClipboardList, ShieldCheck, Crown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const ROLES = [
  { id: 'admin', label: 'Practice Admin', desc: 'Catalog, billing, settings, audit', icon: ShieldCheck, path: '/admin' },
  { id: 'doctor', label: 'Doctor', desc: 'Review queue, AI panel, sign documents', icon: Stethoscope, path: '/doctor' },
  { id: 'nurse', label: 'Medical staff', desc: 'Submission queue, clinical fields, send to doctor', icon: ClipboardList, path: '/nurse' },
  { id: 'owner', label: 'Platform Owner', desc: 'All practices, revenue, support desk', icon: Crown, path: '/owner' },
];

// Demo-only role switcher: visible exclusively in the seeded demo practice.
// It performs a real demo login (JWT) — there is no client-side role spoofing.
export function RoleSwitcher() {
  const { role, user, practice, demoLogin } = useApp();
  const navigate = useNavigate();

  if (!practice?.is_demo) return null;

  const current = ROLES.find(r => r.id === role) || ROLES[0];
  const Icon = current.icon;

  const switchTo = async (r) => {
    try {
      await demoLogin(r.id);
      navigate(r.path);
    } catch (e) {
      toast.error('Could not switch demo role');
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" data-testid="global-role-switcher" className="gap-2">
          <Icon className="h-4 w-4 text-primary" />
          <span className="hidden sm:inline">{current.label}</span>
          <span className="hidden xl:inline text-muted-foreground">— {user?.name || ''}</span>
          <ChevronDown className="h-4 w-4 opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72">
        <DropdownMenuLabel>Demo Role Switcher</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {ROLES.map((r) => {
          const I = r.icon;
          return (
            <DropdownMenuItem
              key={r.id}
              data-testid={`role-option-${r.id}`}
              onClick={() => switchTo(r)}
              className={`flex items-start gap-3 py-2 ${role === r.id ? 'bg-accent' : ''}`}
            >
              <I className="h-4 w-4 text-primary mt-0.5" />
              <div className="flex-1">
                <div className="text-sm font-medium">{r.label}</div>
                <div className="text-xs text-muted-foreground">{r.desc}</div>
              </div>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
